// Platform Administrators: which actions a row offers and what each confirmation says. The server
// enforces everything (strong session, never yourself, at least one active administrator); this
// only hides useless buttons and explains why.
import type { PlatformAdminOverview, PlatformAdministrator } from '../../services/platformAdmins';

export const STATUS_LABEL: Record<PlatformAdministrator['status'], string> = {
  active: 'Active',
  disabled: 'Disabled',
  not_linked: 'No sign-in method with MFA',
};

export interface RowActions {
  disable: boolean;
  enable: boolean;
  remove: boolean;
  /** Bootstrap finalization (also for yourself: it only removes legacy state). */
  finalize: boolean;
  /** Why actions are unavailable (shown instead of buttons). */
  reason?: string;
}

export function rowActions(a: PlatformAdministrator, ov: Pick<PlatformAdminOverview, 'active_count'>): RowActions {
  const finalize = !!a.bootstrap_pending && a.status === 'active';
  if (a.self) return { disable: false, enable: false, remove: false, finalize, reason: 'This is you. Another platform administrator must change your access.' };
  if (a.status === 'active' && ov.active_count <= 1) {
    return { disable: false, enable: false, remove: false, finalize, reason: 'The last active platform administrator cannot be disabled or removed.' };
  }
  return { disable: a.status !== 'disabled', enable: a.status === 'disabled', remove: true, finalize };
}

/** What bootstrap finalization does (shown before confirming). */
export function describeFinalize(a: PlatformAdministrator): { title: string; points: string[]; confirm: string } {
  const who = a.display_name || a.username;
  return {
    title: `Finalize bootstrap administrator ${who}?`,
    points: [
      `${who} keeps its Microsoft account${a.microsoft_account ? ` (${a.microsoft_account})` : ''} and stays a platform administrator.`,
      'Its old local RadioOS password is removed permanently; it signs in with Microsoft only.',
      'It no longer belongs to a customer or network. The customer, its networks, radios and history are not changed.',
      'Microsoft sessions continue; any other session ends.',
    ],
    confirm: 'Finalize',
  };
}

export function describeRevoke(a: PlatformAdministrator, kind: 'disable' | 'remove'): { title: string; points: string[]; confirm: string } {
  const who = a.display_name || a.username;
  const account = a.microsoft_account ? ` (${a.microsoft_account})` : '';
  const points = [
    `Platform administrator: ${who}${account}.`,
    'RadioOS Platform Administrator access is revoked immediately.',
    kind === 'disable' ? 'All existing RadioOS sessions of this administrator end now.' : 'Sessions end now if the person has no other access left.',
    'The Microsoft account itself is NOT changed or deleted.',
  ];
  if (kind === 'disable') points.push('The administrator is kept and can be enabled again later.');
  else points.push('Only the platform administrator role is removed: the person keeps any other access (OEM, customer). Without any other access the person is disabled - kept, never deleted. The administration audit keeps the history.');
  return {
    title: `${kind === 'disable' ? 'Disable' : 'Remove'} platform administrator ${who}?`,
    points,
    confirm: kind === 'disable' ? 'Disable administrator' : 'Remove administrator',
  };
}

export const INVITE_EXPLANATION = [
  'Enter the person\'s e-mail address. They accept the one-time invitation with Microsoft, Google or a RadioOS password - always with multi-factor authentication, which platform administration requires.',
  'With e-mail configured the invitation is e-mailed; otherwise you see the link once and hand it over.',
  'A pending invitation grants no access. It expires after 72 hours.',
  'Someone who already uses RadioOS gets the role on the People page instead (never a second account).',
];
